import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import {
  AppBar, Avatar, Box, Chip, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Menu, MenuItem, Select, Toolbar, Typography, Alert,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import FolderCopyRoundedIcon from "@mui/icons-material/FolderCopyRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useSession } from "../context/SessionContext";
import { ALL_DEPARTMENTS } from "../services/clientSelection";

const DRAWER_WIDTH = 268;

const NAV = [
  { to: "/", label: "Dashboard", icon: <SpaceDashboardRoundedIcon />, end: true },
  { to: "/ai-inventory", label: "Applications", icon: <SmartToyRoundedIcon /> },
  { to: "/frameworks", label: "Frameworks & Controls", icon: <VerifiedUserRoundedIcon /> },
  { to: "/evidence", label: "Evidence", icon: <FolderCopyRoundedIcon /> },
  { to: "/risks", label: "Risk Register", icon: <WarningAmberRoundedIcon /> },
  { to: "/policies", label: "Policies", icon: <DescriptionRoundedIcon /> },
  { to: "/audits", label: "Audits & Findings", icon: <GavelRoundedIcon /> },
  { to: "/vendors", label: "Vendors", icon: <StorefrontRoundedIcon /> },
];

// Only platform admins see this; the server also refuses everyone else.
const ADMIN_NAV = [{ to: "/admin/users", label: "Users & approvals", icon: <ManageAccountsRoundedIcon /> }];

function Brand() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 3 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 3,
          display: "grid",
          placeItems: "center",
          color: "#fff",
          background: "linear-gradient(135deg, #6366F1 0%, #A855F7 55%, #0EA5E9 100%)",
          boxShadow: "0 8px 20px rgba(99,102,241,0.45)",
        }}
      >
        <ShieldRoundedIcon />
      </Box>
      <Box>
        <Typography sx={{ color: "#fff", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          VeraInsight
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.6)", fontWeight: 600 }}>
          GRC Platform
        </Typography>
      </Box>
    </Box>
  );
}

function SidebarContent({ onNavigate, me, email }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        color: "#E2E8F0",
        background: "linear-gradient(180deg, #0B1020 0%, #141233 60%, #1B1547 100%)",
      }}
    >
      <Brand />
      <List sx={{ px: 1.5, flex: 1 }}>
        {[...NAV, ...(me.roles.includes("PlatformAdmin") ? ADMIN_NAV : [])].map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            sx={{
              borderRadius: 2.5,
              mb: 0.5,
              py: 1.1,
              color: "rgba(226,232,240,0.72)",
              "& .MuiListItemIcon-root": { color: "inherit", minWidth: 40 },
              "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "#fff" },
              "&.active": {
                color: "#fff",
                background: "linear-gradient(135deg, rgba(99,102,241,0.9), rgba(139,92,246,0.85))",
                boxShadow: "0 8px 20px rgba(99,102,241,0.35)",
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14.5 }} />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar sx={{ bgcolor: "#6366F1", width: 38, height: 38, fontWeight: 700 }}>
          {(email || "?").charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
            {email}
          </Typography>
          <Typography noWrap variant="caption" sx={{ color: "rgba(226,232,240,0.6)" }}>
            {me.roles.length ? me.roles.join(", ") : "No role assigned"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function Layout() {
  const { me, clientId, isAll, setClientId, authUser, logout, authMode } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  // A record (such as a questionnaire) belongs to one department. When the department changes,
  // leave that record's page, otherwise it would look for an id that doesn't exist in the new one.
  function switchDepartment(value) {
    setClientId(value);
    if (/^\/ai-inventory\/[^/]+/.test(location.pathname)) navigate("/ai-inventory");
  }
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const email = authUser?.email || me.email;
  const multipleClients = me.availableClients.length > 1;
  const canSeeAll = multipleClients && me.staff;
  const currentClient = me.availableClients.find((c) => String(c.id) === String(clientId));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          ml: { md: `${DRAWER_WIDTH}px` },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          backgroundColor: "rgba(244,246,251,0.82)",
          backdropFilter: "blur(14px)",
          color: "text.primary",
          borderBottom: "1px solid rgba(15,23,42,0.06)",
        }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ display: { md: "none" } }} aria-label="Open menu">
            <MenuRoundedIcon />
          </IconButton>
          <Box sx={{ flex: 1 }} />

          {multipleClients ? (
            <Select
              size="small"
              value={String(clientId)}
              onChange={(e) => switchDepartment(e.target.value)}
              startAdornment={<BusinessRoundedIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />}
              sx={{ minWidth: 240, borderRadius: 3, backgroundColor: "#fff", fontWeight: 600 }}
              inputProps={{ "aria-label": "Switch department" }}
            >
              {canSeeAll && (
                <MenuItem value={ALL_DEPARTMENTS} sx={{ fontWeight: 700 }}>
                  All departments
                </MenuItem>
              )}
              {me.availableClients.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            currentClient && <Chip icon={<BusinessRoundedIcon />} label={currentClient.name} sx={{ backgroundColor: "#fff" }} />
          )}

          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Account menu">
            <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36, fontWeight: 700 }}>
              {(email || "?").charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {me.roles.join(", ")}
              </Typography>
            </Box>
            <Divider />
            {authMode === "cognito" ? (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  logout();
                }}
              >
                <LogoutRoundedIcon fontSize="small" sx={{ mr: 1.5 }} /> Sign out
              </MenuItem>
            ) : (
              <MenuItem disabled>Demo mode – no sign-in</MenuItem>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: 0 } }}
        >
          <SidebarContent onNavigate={() => setMobileOpen(false)} me={me} email={email} />
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{ display: { xs: "none", md: "block" }, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: 0 } }}
        >
          <SidebarContent me={me} email={email} />
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 } }}>
        <Toolbar />
        {isAll && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
            Showing all departments together, read-only. Choose a department to register or change anything.
          </Alert>
        )}
        <ErrorBoundary resetKey={location.pathname}><Outlet /></ErrorBoundary>
      </Box>
    </Box>
  );
}
